import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Search, Check, Link2, ShieldAlert, Loader2 } from 'lucide-react';
import { appAuthorizeApi, type AuthorizeInfo, type AuthorizeSettingsSchema } from '../../api/endpoints/appAuthorize';
import { storesApi } from '../../api/endpoints/stores';
import { ZeroMark } from '../../components/layout/ZeroLogo';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { getStoreLogoUrl } from '../../lib/formatters';
import { openAuthPrompt } from '../../lib/authPrompt';
import { ApiError } from '../../types/api';

/**
 * App install consent screen — the page a third-party app sends a merchant to.
 *
 * Reached at /apps/authorize, which the backend forwards here; it lives outside
 * DashboardLayout because there is no store in scope until the merchant picks
 * one. Two phases:
 *   1. pick    — no store_id in the URL: choose which store to install on.
 *   2. consent — store_id set: review scopes, fill app settings, allow or deny.
 *
 * A third phase that renders nothing: when the app is ALREADY installed on this
 * store with these scopes, the consent screen is skipped and the code is issued
 * straight away. Asking a merchant to re-approve permissions they granted weeks
 * ago every time the app links back to it teaches them to click Allow without
 * reading, which is the opposite of what a consent screen is for.
 *
 * Nothing here is a security boundary. `GET/POST /api/v1/apps/oauth/authorize`
 * re-checks auth, the redirect_uri allow-list, the scope catalog, and the
 * install permission; this screen only fails early with a readable message.
 */

function AppLogo({ name, logoUrl }: { name: string; logoUrl?: string }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        className="h-14 w-14 flex-none rounded-2xl border border-border-strong bg-bg-tertiary object-cover"
      />
    );
  }
  return (
    <div className="grid h-14 w-14 flex-none place-items-center rounded-2xl bg-accent-light text-xl font-bold text-accent">
      {[...name][0]?.toUpperCase() ?? '?'}
    </div>
  );
}

/** Zero mark ↔ app logo, the "you are connecting these two" header motif. */
function ConnectHeader({ app }: { app: AuthorizeInfo['app'] }) {
  return (
    <div className="flex items-center gap-3.5">
      <div className="grid h-14 w-14 flex-none place-items-center rounded-2xl border border-border-strong bg-bg-secondary text-text-primary">
        <ZeroMark className="h-6 w-auto" />
      </div>
      <Link2 className="h-[18px] w-[18px] flex-none text-text-muted" strokeWidth={2} />
      <AppLogo name={app.name} logoUrl={app.logoUrl} />
    </div>
  );
}

/** Shared card chrome so every phase lands in the same frame. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg-primary px-4 py-8 text-text-primary">
      <div className="flex max-h-[min(780px,calc(100dvh-64px))] w-full max-w-[480px] flex-col overflow-hidden rounded-3xl border border-border-default bg-bg-secondary shadow-2xl">
        {children}
      </div>
    </div>
  );
}

function ErrorCard({ message, onRetry, retryLabel }: { message: string; onRetry?: () => void; retryLabel?: string }) {
  return (
    <Shell>
      <div className="flex flex-col items-center gap-4 px-8 py-14 text-center">
        <div className="grid h-16 w-16 place-items-center rounded-full bg-danger/15 text-danger">
          <ShieldAlert className="h-8 w-8" strokeWidth={2} />
        </div>
        <p className="text-sm leading-7 text-text-secondary">{message}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="cursor-pointer rounded-xl border border-border-strong bg-bg-tertiary px-6 py-2.5 text-sm font-semibold text-text-primary transition-colors hover:bg-bg-hover"
          >
            {retryLabel}
          </button>
        )}
      </div>
    </Shell>
  );
}

export function AppAuthorizePage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const clientId = searchParams.get('client_id') || '';
  const redirectUri = searchParams.get('redirect_uri') || '';
  const scopeParam = searchParams.get('scope');
  const state = searchParams.get('state');
  const storeIdParam = searchParams.get('store_id');

  const paramsValid = !!clientId && !!redirectUri;

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [settings, setSettings] = useState<Record<string, unknown> | null>(null);
  const [submitting, setSubmitting] = useState<'grant' | 'deny' | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: info, isLoading, error } = useQuery({
    queryKey: ['app-authorize', clientId, redirectUri, scopeParam, storeIdParam],
    queryFn: () =>
      appAuthorizeApi.info({
        client_id: clientId,
        redirect_uri: redirectUri,
        scope: scopeParam,
        state,
        store_id: storeIdParam,
      }),
    enabled: paramsValid,
    retry: false,
  });

  // Only needed for the picker phase.
  const { data: storesData, isLoading: storesLoading } = useQuery({
    queryKey: ['stores'],
    queryFn: storesApi.list,
    enabled: paramsValid && !storeIdParam,
  });

  const schema: AuthorizeSettingsSchema = info?.app.settingsSchema || {};
  const schemaKeys = useMemo(() => Object.keys(schema), [schema]);

  // Seed the settings form once the app + any saved installation values arrive.
  const settingsValues =
    settings ??
    schemaKeys.reduce<Record<string, unknown>>((acc, key) => {
      const saved = info?.existingSettings?.[key];
      acc[key] = saved !== undefined ? saved : schema[key].type === 'boolean' || schema[key].type === 'checkbox' ? false : '';
      return acc;
    }, {});

  const setSetting = (key: string, value: unknown) => setSettings({ ...settingsValues, [key]: value });

  const stores = storesData?.stores ?? [];
  const filteredStores = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((s) => s.name?.toLowerCase().includes(q) || s.id?.toLowerCase().includes(q));
  }, [stores, query]);

  async function submit(action: 'grant' | 'deny') {
    if (!info?.store) return;

    if (action === 'grant') {
      const missing = schemaKeys.find((key) => {
        const f = schema[key];
        if (!f.required || f.type === 'boolean' || f.type === 'checkbox') return false;
        const v = settingsValues[key];
        return v === undefined || v === null || v === '';
      });
      if (missing) {
        setSubmitError(t('appAuthorize.fieldRequired', { field: schema[missing].label || missing }));
        return;
      }
    }

    setSubmitError(null);
    setSubmitting(action);
    try {
      const { redirect } = await appAuthorizeApi.submit({
        client_id: clientId,
        redirect_uri: redirectUri,
        store_id: info.store.id,
        scopes: info.requestedScopes.map((s) => s.scope),
        state,
        action,
        settings: settingsValues,
      });
      // Leaving for the app's own callback. Keep the success frame up so the
      // handoff does not flash the form again while the browser navigates.
      setRedirecting(true);
      window.location.href = redirect;
    } catch (e) {
      setSubmitError(e instanceof ApiError ? e.message : t('common.error'));
      setSubmitting(null);
    }
  }

  /**
   * Re-authorize without asking, when there is genuinely nothing to ask.
   *
   * All three conditions have to hold: the app is installed and active on this
   * store, every scope it is requesting was already granted, and it has no
   * settings form to fill. A NEW scope, or a form field, brings the consent
   * screen straight back — that is the whole point of the check.
   */
  const autoGranted = useRef(false);
  const canSilentlyGrant = Boolean(
    info?.isAlreadyInstalled
    && info.store
    && schemaKeys.length === 0
    && info.requestedScopes.every((s) => info.existingScopes.includes(s.scope)),
  );

  useEffect(() => {
    if (!canSilentlyGrant || autoGranted.current) return;
    autoGranted.current = true;
    submit('grant');
    // `submit` closes over state that does not change before this fires once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSilentlyGrant]);

  if (!paramsValid) {
    return <ErrorCard message={t('appAuthorize.badLink')} />;
  }

  if (error) {
    const unauth = error instanceof ApiError && error.status === 401;
    // A store-specific failure (no install permission, wrong store) is
    // recoverable — drop store_id and the picker comes back.
    const canPickAnother = !unauth && !!storeIdParam;
    const clearStore = () =>
      setSearchParams((prev) => {
        prev.delete('store_id');
        return prev;
      });
    return (
      <ErrorCard
        message={unauth ? t('appAuthorize.signinRequired') : error instanceof ApiError ? error.message : t('common.error')}
        onRetry={unauth ? () => openAuthPrompt('signin') : canPickAnother ? clearStore : undefined}
        retryLabel={unauth ? t('appAuthorize.signin') : t('appAuthorize.pickAnother')}
      />
    );
  }

  if (isLoading || !info) {
    return (
      <Shell>
        <div className="grid place-items-center px-8 py-20">
          <LoadingSpinner />
        </div>
      </Shell>
    );
  }

  // ── Success handoff ───────────────────────────────────────────────────────
  if (canSilentlyGrant && !submitError) {
    return (
      <Shell>
        <div className="grid place-items-center px-8 py-20">
          <LoadingSpinner />
        </div>
      </Shell>
    );
  }

  if (redirecting) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-[18px] px-8 py-14 text-center">
          <div className="grid h-[72px] w-[72px] place-items-center rounded-full bg-accent-light text-accent">
            {submitting === 'grant' ? (
              <Check className="h-8 w-8" strokeWidth={2.5} />
            ) : (
              <Loader2 className="h-8 w-8 animate-spin" strokeWidth={2.5} />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <h2 className="text-[22px] font-bold">
              {submitting === 'grant' ? t('appAuthorize.doneTitle') : t('appAuthorize.denied')}
            </h2>
            <p className="text-sm leading-7 text-text-muted">
              {submitting === 'grant'
                ? t('appAuthorize.doneBody', { store: info.store?.name ?? '' })
                : t('appAuthorize.returning')}
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  // ── Phase 1: pick a store ─────────────────────────────────────────────────
  if (!info.store) {
    // The first store is preselected rather than leaving the button dead. Most
    // merchants have exactly one, and for them the pick step is a question with
    // a single possible answer that they still had to click. Preselecting keeps
    // it visible and changeable — the list is right there — while turning a
    // required click into an optional one.
    const chosen = stores.find((s) => s._id === selected) ?? stores[0];
    return (
      <Shell>
        <div className="flex flex-col items-center gap-4 px-7 pb-5 pt-8 text-center">
          <ConnectHeader app={info.app} />
          <div className="flex flex-col gap-1.5">
            {/* The app's registered name stands alone as the title — app names
                often already carry their own verb ("ربط ديسكورد"), so prefixing
                one here would read as a stutter. */}
            <h1 className="text-2xl font-bold leading-tight">{info.app.name}</h1>
            <p className="text-sm leading-relaxed text-text-muted">{t('appAuthorize.pickStore')}</p>
          </div>
        </div>

        {stores.length > 6 && (
          <div className="relative px-7 pb-3.5">
            <Search
              className="pointer-events-none absolute top-[13px] h-4 w-4 text-text-muted"
              style={{ insetInlineStart: '42px' }}
              strokeWidth={2}
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('appAuthorize.searchStores')}
              className="w-full rounded-[10px] border border-border-strong bg-bg-primary py-[11px] pe-3.5 ps-10 text-sm text-text-primary outline-none transition-colors placeholder:text-text-disabled focus:border-accent"
            />
          </div>
        )}

        <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-7 pb-4 pt-1">
          <div className="flex items-center justify-between pb-0.5">
            <span className="text-xs font-semibold tracking-[0.08em] text-text-muted">{t('appAuthorize.yourStores')}</span>
            {stores.length > 0 && (
              <span className="inline-flex rounded-full bg-accent-light px-2.5 py-[3px] text-xs font-semibold text-accent">
                {t('appAuthorize.storeCount', { count: stores.length })}
              </span>
            )}
          </div>

          {storesLoading ? (
            <div className="grid place-items-center py-10">
              <LoadingSpinner />
            </div>
          ) : filteredStores.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-text-muted">
              {stores.length === 0 ? t('appAuthorize.noStores') : t('appAuthorize.noResults')}
            </div>
          ) : (
            filteredStores.map((store) => {
              // Matches `chosen` above, so the highlighted row and the button's
              // target are never two different stores.
              const isSelected = chosen?._id === store._id;
              return (
                <button
                  type="button"
                  key={store._id}
                  onClick={() => setSelected(store._id)}
                  className={`flex cursor-pointer items-center gap-3 rounded-[14px] border p-[10px_12px] text-start transition-colors ${
                    isSelected
                      ? 'border-accent-action bg-accent-light'
                      : 'border-border-default bg-bg-tertiary hover:border-border-strong'
                  }`}
                >
                  {store.logo ? (
                    <img
                      src={getStoreLogoUrl(store.logo, 80)}
                      alt=""
                      className="h-10 w-10 flex-none rounded-xl object-cover"
                    />
                  ) : (
                    <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-accent-light text-base font-bold text-accent">
                      {[...(store.name || '?')][0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-px">
                    <span className="truncate text-[15px] font-semibold leading-tight">{store.name}</span>
                    <span dir="ltr" className="self-start font-numeric text-[12.5px] text-text-muted">
                      {store.id || '·'}
                    </span>
                  </div>
                  {isSelected && (
                    <div className="grid h-[22px] w-[22px] flex-none place-items-center rounded-full bg-accent-action text-white">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>

        <div className="flex flex-col gap-2.5 border-t border-border-default px-7 pb-6 pt-4">
          <button
            type="button"
            disabled={!chosen}
            onClick={() => {
              if (!chosen) return;
              setSearchParams((prev) => {
                prev.set('store_id', chosen._id);
                return prev;
              });
            }}
            className="w-full cursor-pointer rounded-xl bg-accent-action py-[13px] text-base font-semibold text-white transition-colors hover:bg-accent-deep disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-accent-action"
          >
            {chosen ? t('appAuthorize.continueWith', { store: chosen.name }) : t('appAuthorize.pickToContinue')}
          </button>
          <p className="text-center text-[12.5px] leading-relaxed text-text-muted">{t('appAuthorize.pickNote')}</p>
        </div>
      </Shell>
    );
  }

  // ── Phase 2: review scopes + settings ─────────────────────────────────────
  const busy = submitting !== null;
  return (
    <Shell>
      <div className="flex flex-col items-center gap-4 px-7 pb-5 pt-8 text-center">
        <ConnectHeader app={info.app} />
        <div className="flex flex-col gap-1.5">
          {/* Same treatment as the picker: name alone, action in the subtitle. */}
          <h1 className="text-2xl font-bold leading-tight">{info.app.name}</h1>
          <p className="text-sm leading-relaxed text-text-muted">{t('appAuthorize.requestSub')}</p>
          {info.app.developerEmail && (
            <p className="text-xs text-text-muted">{t('appAuthorize.by', { developer: info.app.developerEmail })}</p>
          )}
          {info.isAlreadyInstalled && (
            <span className="mx-auto mt-1 inline-flex rounded-full bg-accent-light px-3 py-1 text-xs font-semibold text-accent">
              {t('appAuthorize.alreadyInstalled')}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-7 pb-4">
        {info.app.description && (
          <p className="text-[13.5px] leading-7 text-text-muted">{info.app.description}</p>
        )}

        <section className="flex flex-col gap-2">
          <span className="text-xs font-semibold tracking-[0.08em] text-text-muted">{t('appAuthorize.store')}</span>
          <div className="flex items-center justify-between gap-3 rounded-[14px] border border-border-default bg-bg-tertiary p-[12px_14px]">
            <span className="truncate text-[15px] font-semibold">{info.store.name}</span>
            <span dir="ltr" className="flex-none font-numeric text-[12.5px] text-text-muted">
              {info.store.slug}
            </span>
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <span className="text-xs font-semibold tracking-[0.08em] text-text-muted">{t('appAuthorize.scopes')}</span>
          <ul className="flex flex-col gap-2">
            {info.requestedScopes.map((s) => (
              <li key={s.scope} className="flex items-start gap-3 rounded-[14px] bg-bg-tertiary p-[10px_12px]">
                <div className="mt-0.5 grid h-[22px] w-[22px] flex-none place-items-center rounded-full bg-accent-light text-accent">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </div>
                <div className="flex min-w-0 flex-col gap-px">
                  <span className="text-[13.5px] leading-relaxed">{s.description}</span>
                  <code dir="ltr" className="self-start font-numeric text-[11.5px] text-text-muted">
                    {s.scope}
                  </code>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {schemaKeys.length > 0 && (
          <section className="flex flex-col gap-3">
            <span className="text-xs font-semibold tracking-[0.08em] text-text-muted">{t('appAuthorize.appSettings')}</span>
            {schemaKeys.map((key) => {
              const f = schema[key];
              const value = settingsValues[key];
              const inputClass =
                'w-full rounded-[10px] border border-border-strong bg-bg-primary px-3.5 py-[10px] text-sm text-text-primary outline-none transition-colors placeholder:text-text-disabled focus:border-accent';

              if (f.type === 'boolean' || f.type === 'checkbox') {
                return (
                  <label key={key} className="flex cursor-pointer items-center gap-2.5 text-[13.5px]">
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => setSetting(key, e.target.checked)}
                      className="h-4 w-4 accent-accent-action"
                    />
                    <span>{f.label || key}</span>
                  </label>
                );
              }

              return (
                <div key={key} className="flex flex-col gap-1.5">
                  <label htmlFor={`set_${key}`} className="text-[13px] font-semibold">
                    {f.label || key}
                    {f.required && <span className="text-danger"> *</span>}
                  </label>
                  {f.type === 'select' ? (
                    <select
                      id={`set_${key}`}
                      value={String(value ?? '')}
                      onChange={(e) => setSetting(key, e.target.value)}
                      className={inputClass}
                    >
                      <option value="">{t('appAuthorize.choose')}</option>
                      {(f.options || []).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label || o.value}
                        </option>
                      ))}
                    </select>
                  ) : f.type === 'textarea' ? (
                    <textarea
                      id={`set_${key}`}
                      value={String(value ?? '')}
                      onChange={(e) => setSetting(key, e.target.value)}
                      placeholder={f.placeholder || ''}
                      className={`${inputClass} min-h-20 resize-y`}
                    />
                  ) : (
                    <input
                      id={`set_${key}`}
                      type={f.type === 'number' ? 'number' : 'text'}
                      value={String(value ?? '')}
                      onChange={(e) => setSetting(key, f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
                      placeholder={f.placeholder || ''}
                      className={inputClass}
                    />
                  )}
                  {(f.description || f.hint) && (
                    <span className="text-xs text-text-muted">{f.description || f.hint}</span>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {(info.app.website || info.app.privacyPolicyUrl) && (
          <div className="flex items-center justify-center gap-3 text-xs text-text-muted">
            {info.app.website && (
              <a href={info.app.website} target="_blank" rel="noopener noreferrer" className="hover:text-accent">
                {t('appAuthorize.appWebsite')}
              </a>
            )}
            {info.app.privacyPolicyUrl && (
              <a href={info.app.privacyPolicyUrl} target="_blank" rel="noopener noreferrer" className="hover:text-accent">
                {t('appAuthorize.privacyPolicy')}
              </a>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2.5 border-t border-border-default px-7 pb-6 pt-4">
        {submitError && <p className="text-center text-[12.5px] text-danger">{submitError}</p>}
        <div className="flex gap-2.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => submit('deny')}
            className="flex-none cursor-pointer rounded-xl border border-border-strong bg-bg-tertiary px-6 py-[13px] text-base font-semibold text-text-primary transition-colors hover:bg-bg-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('appAuthorize.deny')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => submit('grant')}
            className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl bg-accent-action py-[13px] text-base font-semibold text-white transition-colors hover:bg-accent-deep disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting === 'grant' && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('appAuthorize.allow')}
          </button>
        </div>
        <p className="text-center text-[12.5px] leading-relaxed text-text-muted">{t('appAuthorize.consentNote')}</p>
      </div>
    </Shell>
  );
}
